import {
  EditFieldCapacityOverrideForm,
  FieldCapacityOverride,
  Actions,
  QueryResult,
  Subjects,
} from '@dispatch/shared/types';
import { isRunningInJest } from '@dispatch/testing';
import {
  ConfirmModal,
  dateColumn,
  Section,
  tableStyleOverrides,
  textColumn,
  useNotification,
} from '@dispatch/web/ui';
import { Stack } from '@mui/material';
import {
  DataGridPro,
  GridActionsCellItem,
  GridRowParams,
  GridValueFormatterParams,
} from '@mui/x-data-grid-pro';
import DeleteIcon from '@mui/icons-material/Delete';
import { useState } from 'react';
import { useDeleteFieldCapacityOverride } from '@dispatch/shared/hooks';
import { Ability } from '@casl/ability';

export type FieldCapacityOverridesProps = {
  resourceId: string;
  overridesQuery?: QueryResult<FieldCapacityOverride[] | undefined, unknown>;
  permissions: Ability<[Actions, Subjects]> | undefined;
  setEditOverrideForm: (
    editOverrideForm: EditFieldCapacityOverrideForm
  ) => void;
  setOpenEditOverride: React.Dispatch<React.SetStateAction<boolean>>;
  setIsEdit: React.Dispatch<React.SetStateAction<boolean>>;
  isReadOnly: boolean;
};

export default function FieldCapacityOverrides({
  resourceId,
  overridesQuery,
  permissions,
  setEditOverrideForm,
  setOpenEditOverride,
  setIsEdit,
  isReadOnly = false,
}: FieldCapacityOverridesProps) {
  const hasUpsertPermissions =
    permissions?.can?.('upsert', 'resource') || false;

  const onClickEditOverride = (row: FieldCapacityOverride) => {
    setIsEdit(true);
    setEditOverrideForm({
      date: row.date,
      capacity: row.capacity,
    });
    setOpenEditOverride(true);
  };

  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
  const { notifyError, notifySuccess } = useNotification();
  const [toDelete, setToDelete] = useState<{
    resourceId: string;
    date: string;
  }>({
    resourceId: '',
    date: '',
  });

  const onDeleteOverride = (response: { success: boolean }) => {
    if (response?.success) {
      notifySuccess('Success', 'Field capacity override was deleted');
      overridesQuery?.refetch && overridesQuery?.refetch();
    } else {
      notifyError('Error', 'Error deleting field capacity override');
    }

    setOpenConfirmDeleteModal(false);
  };
  const { deleteFieldCapacityOverride } =
    useDeleteFieldCapacityOverride(onDeleteOverride);

  const onDeleteClick = (resourceId: string, date: string) => {
    if (resourceId && date) {
      setOpenConfirmDeleteModal(true);
      setToDelete({
        resourceId,
        date,
      });
    } else {
      notifyError('Error', 'Error deleting field capacity override');
    }
  };

  const renderConfirmDelete = () => {
    return (
      <ConfirmModal
        onConfirm={() => deleteFieldCapacityOverride(toDelete)}
        onDeny={() => setOpenConfirmDeleteModal(false)}
        confirmTitle={'Delete Field Capacity Override?'}
        confirmContent={
          'Deleting the selected override will restore this day to the resource’s standard field capacity'
        }
        modalType={'delete'}
      />
    );
  };

  const columns = [
    dateColumn(
      {
        field: 'date',
        headerName: 'Date',
        valueGetter: (params) =>
          params.row?.date ? new Date(params.row.date) : null,
      },
      'MM/dd/yyyy',
      'field-capacity-override-row'
    ),
    textColumn(
      {
        field: 'capacity',
        headerName: 'Capacity',
        // `textColumn` renders only strings -- it emits an empty cell for any
        // non-string formatted value -- and capacity arrives from the API as a
        // number, so it has to be stringified here or the cell reads blank.
        // Done in valueFormatter rather than valueGetter deliberately: the
        // underlying value stays numeric so sorting stays numeric (a string
        // value would order "10" before "8"). `!= null` rather than a
        // truthiness check keeps a real 0 override rendering as "0".
        valueFormatter: (params: GridValueFormatterParams) =>
          params?.value != null ? `${params.value}` : '',
      },
      'field-capacity-override-capacity'
    ),
    {
      field: 'actions',
      type: 'actions',
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="DELETE"
          disabled={isReadOnly}
          onClick={() => onDeleteClick(resourceId, params.row?.date)}
        />,
      ],
    },
  ];

  return (
    <Section>
      <div
        style={{ height: '400px' }}
        data-testid="field-capacity-overrides-list"
      >
        <DataGridPro
          disableVirtualization={isRunningInJest()}
          loading={overridesQuery?.status === 'loading'}
          rows={overridesQuery?.data || []}
          components={{
            NoRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                No Overrides
              </Stack>
            ),
            NoResultsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                No Overrides Found
              </Stack>
            ),
          }}
          columns={columns}
          sx={tableStyleOverrides}
          getRowId={(row) => row.date}
          hideFooter={isRunningInJest()}
          hideFooterSelectedRowCount={true}
          disableSelectionOnClick={true}
          onRowClick={(value) => {
            hasUpsertPermissions &&
              !isReadOnly &&
              onClickEditOverride(value.row);
          }}
          columnVisibilityModel={{
            actions: hasUpsertPermissions,
          }}
          pagination
          autoPageSize
        />
      </div>
      {openConfirmDeleteModal && renderConfirmDelete()}
    </Section>
  );
}
